import { toast } from "react-toastify";
import {
  reportAsyncError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { rebuildQuery } from "../../../../utils/url-utils";
import apiClient from "../../../apis/api-client";
import { BaseApplicationService } from "@graffer-inc/smart-apply";
import {
  DefaultNumItemsPerPages,
  paging,
  toTotalPage,
} from "../../../domain/pagination/paging";
import {
  BaseProcedureEntity,
  ProcedureDetailType,
} from "../../../domain/procedures/procedure-entity";
import { PublishStatus } from "@graffer-inc/cloud-gov-core";
import { CloudGovApiError } from "../../../domain/api-error";
import { ProcedureImportStatusType } from "../../../domain/procedures/procedure-import-status";
import {
  downloadCSVForUpdateGroup,
  bulkUpdateGroupPreview,
  bulkUpdateGroup,
  UpdateProcedureGroupPreviewResponse,
  UpdateProcedureGroupResponse,
} from "../../../domain/procedures/procedure-update-group";

export type ProceduresContextProps = {
  isLoading: boolean;
  procedures: Array<BaseProcedureEntity>;
  paging: paging;
  selectedStatus: PublishStatus[];
  queryName: string;
  setPage: typeof ProceduresService.prototype.setPage;
  movePage: typeof ProceduresService.prototype.movePage;
  setName: typeof ProceduresService.prototype.setName;
  setSelectedStatus: typeof ProceduresService.prototype.setSelectedStatus;
  fetchProcedures: typeof ProceduresService.prototype.fetchProcedures;
  createProcedure: typeof ProceduresService.prototype.createProcedure;
  searchProcedures: typeof ProceduresService.prototype.searchProcedures;
  importProcedureFromZipFile: typeof ProceduresService.prototype.importProcedureFromZipFile;
  checkProcedureImportZipFile: typeof ProceduresService.prototype.checkProcedureImportZipFile;
  onDownloadCsvForUpdateGroup: typeof ProceduresService.prototype.onDownloadCSVForUpdateGroup;
  bulkUpdateGroupPreview: typeof ProceduresService.prototype.bulkUpdateGroupPreview;
  bulkUpdateGroup: typeof ProceduresService.prototype.bulkUpdateGroup;
};

export class ProceduresService extends BaseApplicationService<ProceduresContextProps> {
  private readonly procedureCountPerPage: number = DefaultNumItemsPerPages;

  state: ProceduresContextProps = {
    isLoading: false,
    procedures: Array<BaseProcedureEntity>(),
    paging: {
      current: 1,
      total: 0,
    },
    queryName: "",
    selectedStatus: [],
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setName: this.setName.bind(this),
    setSelectedStatus: this.setSelectedStatus.bind(this),
    fetchProcedures: this.fetchProcedures.bind(this),
    createProcedure: this.createProcedure.bind(this),
    searchProcedures: this.searchProcedures.bind(this),
    importProcedureFromZipFile: this.importProcedureFromZipFile.bind(this),
    checkProcedureImportZipFile: this.checkProcedureImportZipFile.bind(this),
    onDownloadCsvForUpdateGroup: this.onDownloadCSVForUpdateGroup.bind(this),
    bulkUpdateGroupPreview: this.bulkUpdateGroupPreview.bind(this),
    bulkUpdateGroup: this.bulkUpdateGroup.bind(this),
  };

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setName(name: string) {
    this.setState({
      queryName: name,
    });
  }

  setSelectedStatus(status: PublishStatus[]) {
    this.setState({
      selectedStatus: status,
    });
  }

  async fetchProcedures() {
    this.setIsLoading(true);
    try {
      const { data: procs, headers } = await apiClient.fetchProcedures(
        this.state.paging.current,
        this.procedureCountPerPage,
        {
          name: this.state.queryName ? this.state.queryName : undefined,
          publishStatus:
            this.state.selectedStatus.length > 0
              ? this.state.selectedStatus
              : undefined,
        },
      );
      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.procedureCountPerPage,
          ),
        },
        procedures: procs || [],
      });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続き一覧の取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async createProcedure(p: ProcedureDetailType) {
    this.setIsLoading(true);
    try {
      const { data: proc } = await apiClient.createProcedure(p);
      this.history.push(`/smart-apply/procedures/${proc.id}`);
      toast.success("手続きを新規に作成しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続き作成に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  searchProcedures(name: string) {
    this.history.replace(
      rebuildQuery({
        page: "1",
        name: name,
      }),
    );
  }

  setIsLoading(isLoading: boolean) {
    this.setState({ isLoading });
  }

  async checkProcedureImportZipFile(
    zipData: File,
  ): Promise<ProcedureImportStatusType | void> {
    try {
      const { data } =
        await apiClient.checkProcedureImportSerializedFile(zipData);
      return data;
    } catch (e) {
      reportAsyncError(e);
      toast.error("インポート用のファイルチェックに失敗しました。");
    }
  }

  async importProcedureFromZipFile(
    zipFile: File,
    shallClearCustomPath: boolean,
    groupId?: string,
  ) {
    try {
      const { data } = await apiClient.importProcedureFromZipFile(
        zipFile,
        shallClearCustomPath,
        groupId,
      );
      toast.success(`手続きをインポートしました。`);
      return data.id;
    } catch (e) {
      if (CloudGovApiError.isErrorCode(e, "custom_url_already_exists")) {
        toast.error(`URLが重複するため、登録できませんでした。`);
        throw e;
      }
      reportAsyncError(e);
      toast.error(`インポートに失敗しました。`);
      throw e;
    }
  }

  async onDownloadCSVForUpdateGroup(
    orgId: string,
    groupIds: string[],
  ): Promise<void | string> {
    return downloadCSVForUpdateGroup(orgId, groupIds);
  }

  async bulkUpdateGroupPreview(
    csv: string,
    orgId: string,
  ): Promise<UpdateProcedureGroupPreviewResponse | string> {
    return bulkUpdateGroupPreview(csv, orgId);
  }

  async bulkUpdateGroup(
    csv: string,
    orgId: string,
  ): Promise<UpdateProcedureGroupResponse | string> {
    return bulkUpdateGroup(csv, orgId);
  }
}
